import React, { useState } from 'react';
import styled from 'styled-components';
import { FaCalendarAlt, FaTimes } from 'react-icons/fa';

const SetDeadlineDialog = ({ session, sessions, onClose, onSave, isBulk = false }) => {
    const [days, setDays] = useState(7); // Default
    const [isLoading, setIsLoading] = useState(false);
    const [onlyNewActivities, setOnlyNewActivities] = useState(true); // Default to only new activities

    // Calculate deadline date
    const calculateDeadline = (numDays) => {
        const deadline = new Date();
        deadline.setDate(deadline.getDate() + numDays);
        return deadline;
    };

    const deadlineDate = calculateDeadline(days);

    let affectedCount;
    if (isBulk) {
        if (onlyNewActivities) {
            affectedCount = sessions?.filter(s => s && !s.reviewDeadline)?.length || 0;
        } else {
            affectedCount = sessions?.length || 0;
        }
    } else {
        affectedCount = 1;
    }

    let totalCount;
    if (isBulk) {
        totalCount = sessions?.length || 0;
    } else {
        totalCount = 1;
    }
    const skippedCount = totalCount - affectedCount;

    const handleSave = async () => {
        if (days < 1) {
            alert('Please enter a valid number of days (minimum 1)');
            return;
        }

        if (isBulk && affectedCount === 0) {
            alert('No activities to update. All activities already have deadlines set.');
            return;
        }

        setIsLoading(true);
        try {
            if (isBulk) {
                let sessionsToUpdate;
                if (onlyNewActivities) {
                    sessionsToUpdate = sessions.filter(s => s && !s.reviewDeadline);
                } else {
                    sessionsToUpdate = sessions;
                }
                await onSave(sessionsToUpdate, days);
            } else {
                await onSave(session.id, days);
            }
            onClose();
        } catch (error) {
            console.error('Error setting deadline:', error);
            alert('Failed to set deadline. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDaysChange = (e) => {
        const value = parseInt(e.target.value);
        if (!isNaN(value) && value >= 1) {
            setDays(value);
        }
    };

    return (
        <Overlay onClick={onClose}>
            <Dialog onClick={(e) => e.stopPropagation()}>
                <Header>
                    <Title>
                        <FaCalendarAlt style={{ marginRight: '8px' }} />
                        {isBulk ? 'Set Deadline for All Activities' : 'Set Review Deadline'}
                    </Title>
                    <CloseButton onClick={onClose}>
                        <FaTimes />
                    </CloseButton>
                </Header>

                <Content>
                    {!isBulk && session && (
                        <>
                            <SessionInfo>
                                <Label>Activity:</Label>
                                <ActivityTitle>{session.title || 'Untitled Activity'}</ActivityTitle>
                            </SessionInfo>
                            {session.reviewDeadline && (
                                <WarningBox>
                                    <strong>Note:</strong> This activity already has a deadline set for{' '}
                                    {new Date(session.reviewDeadline).toLocaleDateString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}. Setting a new deadline will overwrite it.
                                </WarningBox>
                            )}
                        </>
                    )}

                    {isBulk && (
                        <>
                            <CheckboxWrapper>
                                <CheckboxLabel>
                                    <input
                                        type="checkbox"
                                        checked={onlyNewActivities}
                                        onChange={(e) => setOnlyNewActivities(e.target.checked)}
                                    />
                                    <span>Only apply to activities without existing deadline</span>
                                </CheckboxLabel>
                                <CheckboxHint>
                                    {onlyNewActivities
                                        ? 'Activities with existing deadlines will be skipped'
                                        : 'This will overwrite all existing deadlines'}
                                </CheckboxHint>
                            </CheckboxWrapper>
                            <SessionInfo>
                                <Label>Applying to:</Label>
                                <ActivityTitle>
                                    {affectedCount} {affectedCount === 1 ? 'activity' : 'activities'}
                                    {onlyNewActivities && skippedCount > 0 && (
                                        <SkippedText>
                                            {' '}({skippedCount} {skippedCount === 1 ? 'activity' : 'activities'} will be skipped)
                                        </SkippedText>
                                    )}
                                </ActivityTitle>
                            </SessionInfo>
                        </>
                    )}

                    <DeadlineSection>
                        <Label>Set deadline in days:</Label>
                        <DaysInputWrapper>
                            <DaysInput
                                type="number"
                                min="1"
                                max="365"
                                value={days}
                                onChange={handleDaysChange}
                            />
                            <DaysLabel>{isBulk ? 'days after activity submission' : 'days from now'}</DaysLabel>
                        </DaysInputWrapper>

                        {!isBulk && (
                            <DeadlinePreview>
                                <PreviewLabel>Deadline will be:</PreviewLabel>
                                <PreviewDate>
                                    {deadlineDate.toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </PreviewDate>
                            </DeadlinePreview>
                        )}
                        {isBulk && (
                            <DeadlinePreview>
                                <PreviewLabel>Deadline calculation:</PreviewLabel>
                                <PreviewDate>
                                    Each activity's submission time + {days} {days === 1 ? 'day' : 'days'} ({days * 24} hours)
                                </PreviewDate>
                                <PreviewNote>
                                    Example: Submitted on Nov 10 at 1:00 PM → Closes on {new Date(new Date(2024, 10, 10, 13, 0).getTime() + days * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at 1:00 PM
                                </PreviewNote>
                            </DeadlinePreview>
                        )}

                        <QuickButtons>
                            <QuickButton onClick={() => setDays(3)}>3 days</QuickButton>
                            <QuickButton onClick={() => setDays(7)}>7 days</QuickButton>
                            <QuickButton onClick={() => setDays(14)}>14 days</QuickButton>
                            <QuickButton onClick={() => setDays(30)}>30 days</QuickButton>
                        </QuickButtons>
                    </DeadlineSection>

                    {/* <InfoBox>
                        <strong>Note:</strong>{' '}
                        {isBulk &&
                            `This will set deadlines for all ${affectedCount} activities. Each deadline will be calculated as the activity's submission time + ${days} ${days === 1 ? 'day' : 'days'}.`
                        }
                    </InfoBox> */}

                </Content>

                <Footer>
                    <CancelButton onClick={onClose} disabled={isLoading}>
                        Cancel
                    </CancelButton>
                    <SaveButton onClick={handleSave} disabled={isLoading}>
                        {isLoading ? 'Setting...' : 'Set Deadline'}
                    </SaveButton>
                </Footer>
            </Dialog>
        </Overlay>
    );
};

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
`;

const Dialog = styled.div`
    background: white;
    border-radius: 12px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    overflow: hidden;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
`;

const Title = styled.h2`
    display: flex;
    align-items: center;
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.25rem;
    color: #6b7280;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;

    &:hover {
        color: #111827;
    }
`;

const Content = styled.div`
    padding: 24px;
`;

const SessionInfo = styled.div`
    margin-bottom: 24px;
    padding: 12px;
    background: #f3f4f6;
    border-radius: 6px;
`;

const Label = styled.div`
    font-size: 0.875rem;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 6px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
`;

const ActivityTitle = styled.div`
    font-size: 1rem;
    font-weight: 500;
    color: #111827;
`;

const DeadlineSection = styled.div`
    margin-bottom: 20px;
`;

const DaysInputWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
`;

const DaysInput = styled.input`
    width: 100px;
    padding: 10px 12px;
    font-size: 1.25rem;
    font-weight: 600;
    border: 2px solid #d1d5db;
    border-radius: 6px;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #3b82f6;
    }
`;

const DaysLabel = styled.span`
    font-size: 1rem;
    color: #6b7280;
`;

const DeadlinePreview = styled.div`
    padding: 16px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    margin-bottom: 16px;
`;

const PreviewLabel = styled.div`
    font-size: 0.875rem;
    color: #1e40af;
    margin-bottom: 4px;
`;

const PreviewDate = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #1e3a8a;
`;

const PreviewNote = styled.div`
  font-size: 0.875rem;
  color: #3b82f6;
  margin-top: 8px;
  font-style: italic;
`;

const QuickButtons = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
`;

const QuickButton = styled.button`
    padding: 8px 12px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
    }
`;

const Footer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 24px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
`;

const CancelButton = styled.button`
    padding: 10px 20px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: #f3f4f6;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SaveButton = styled.button`
    padding: 10px 24px;
    background: #3b82f6;
    border: none;
    border-radius: 6px;
    font-size: 0.875rem;
    font-weight: 500;
    color: white;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: #2563eb;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const CheckboxWrapper = styled.div`
    margin-bottom: 20px;
    padding: 16px;
    background: #fef9e7;
    border: 1px solid #fde68a;
    border-radius: 8px;
`;

const CheckboxLabel = styled.label`
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    font-size: 0.95rem;
    font-weight: 500;
    color: #111827;
    margin-bottom: 8px;

    input[type="checkbox"] {
        width: 18px;
        height: 18px;
        cursor: pointer;
    }

    span {
        user-select: none;
    }
`;

const CheckboxHint = styled.div`
    font-size: 0.8rem;
    color: #92400e;
    margin-left: 28px;
    font-style: italic;
`;

const SkippedText = styled.span`
    color: #6b7280;
    font-size: 0.875rem;
    font-weight: 400;
`;

const WarningBox = styled.div`
    padding: 12px;
    background: #fef3c7;
    border: 1px solid #fde047;
    border-radius: 6px;
    font-size: 0.875rem;
    color: #92400e;
    line-height: 1.5;
    margin-bottom: 20px;

    strong {
        font-weight: 600;
    }
`;

export default SetDeadlineDialog;
